import type { User } from '@/types/user';

/**
 * Decides which page renders first after a login (or on visiting `/`) based on
 * the caller's cached role. This is NOT an authorization control — `roleName`
 * is a server-issued value (set from the caller's Role record, see
 * `api/src/modules/users/dto/user.dto.ts`'s `UserDto.fromUser`) but it is
 * cached client-side in `localStorage.adminUser`, which is fully editable by
 * the user in devtools. A tampered `roleName` changes only which page renders
 * first; every member read stays enforced server-side by
 * `MembersService.assertCoachCohortScope` (357-01) and
 * `UserPermissionsService.canAccessMemberDirectory` (357-02).
 */

// Mirrors `COACH_NAME` in `api/src/constants/constants.ts` — the two must
// change together.
export const COACH_ROLE_NAME = 'coach';

export const COACH_LANDING_ROUTE = '/my-cohort';
export const DEFAULT_LANDING_ROUTE = '/';

/** True only when the user's server-issued roleName is an exact match for `coach`. */
export function isCoachRole(user?: Pick<User, 'roleName'> | null): boolean {
  return user?.roleName === COACH_ROLE_NAME;
}

/**
 * Returns the route a caller should land on immediately after authenticating
 * (or when revisiting `/`). Never throws — `null`/`undefined` resolve to the
 * default route, same as any non-coach role.
 */
export function resolveLandingRoute(user?: Pick<User, 'roleName'> | null): string {
  return isCoachRole(user) ? COACH_LANDING_ROUTE : DEFAULT_LANDING_ROUTE;
}

/**
 * Returns the cohort roster destination for a sponsoring coach's `coachId`,
 * conditioned on the caller's cached role. This is a UX convenience only,
 * NOT an authorization control: `/coaches/:coachId/cohort` is an admin-only
 * endpoint that rejects coach-role callers with a server-side 401 (Phase 356),
 * so this branch exists purely to avoid sending a coach-role viewer to a URL
 * that would 401 them. Tampering with the cached `roleName` cannot grant
 * access — the server-side check is the real boundary, same framing as this
 * file's header comment.
 */
export function resolveCoachCohortRoute(
  user: Pick<User, 'roleName'> | null | undefined,
  coachId: string,
): string {
  return isCoachRole(user) ? COACH_LANDING_ROUTE : `/coaches/${coachId}/cohort`;
}

/**
 * Reads the cached user from `localStorage.adminUser`, matching the parsing
 * pattern `useAuth`'s `loadAuthFromStorage` uses. Returns `null` when the key
 * is absent or the stored JSON is malformed. Does not clear storage on a
 * malformed value — callers already own that cleanup (see `LoginPage`'s and
 * `LoginPageLegacy`'s still-valid-token effect).
 */
export function readStoredUser(): User | null {
  const stored = localStorage.getItem('adminUser');
  if (!stored) return null;
  try {
    return JSON.parse(stored) as User;
  } catch {
    return null;
  }
}
